// ═══════════════════════════════════════════════════════════════
// documentsApi.js — Client /api/convert/* (Module Documents)
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

export class DocumentsApiError extends Error {
    constructor(status, code, message, details) {
        super(message || code || `HTTP ${status}`);
        this.name = 'DocumentsApiError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new DocumentsApiError(
                res.status,
                data.code || data.error,
                data.error || data.message || `HTTP ${res.status}`,
                data.details
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

export const listConverters = () => request('/api/convert/converters');

export const getLimits = () => request('/api/convert/limits');

export const getJob = (jobId) => request(`/api/convert/${jobId}`);

export async function createConvertJob(file, { from, to, language, palette, title } = {}) {
    const form = new FormData();
    form.append('file', file);
    if (to) form.append('to', to);
    if (from) form.append('from', from);
    if (language) form.append('language', language);
    if (palette) form.append('palette', palette);
    if (title) form.append('title', title);

    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 120000);
    try {
        const res = await fetch('/api/convert', {
            method: 'POST',
            headers: { Authorization: `Bearer ${getToken()}` },
            body: form,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new DocumentsApiError(
                res.status,
                data.error,
                data.error || `HTTP ${res.status}`,
                data.details
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

/** @deprecated Utiliser createConvertJob */
export const createOcrJob = (file, opts = {}) =>
    createConvertJob(file, { from: 'pdf', ...opts });

export async function signDownloadUrl(downloadPath) {
    const data = await request('/api/sign-url', {
        method: 'POST',
        body: { path: downloadPath },
    });
    return data.url;
}

export async function pollJob(jobId, { intervalMs = 2000, maxAttempts = 90, onTick } = {}) {
    for (let i = 0; i < maxAttempts; i++) {
        const job = await getJob(jobId);
        if (onTick) onTick(job);
        if (job.status === 'done' || job.status === 'error') return job;
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new DocumentsApiError(408, 'timeout', 'Conversion trop longue — réessayez plus tard');
}
